import { UI_MESSAGES } from '@hprofits/ui';

import legacyEn from '../../i18n/en.json';
import pocEn from './locales/poc.en.json';

export const APP_LOCALES = ['en', 'ru'] as const;

export type AppLocale = (typeof APP_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'en';

export const isAppLocale = (value: unknown): value is AppLocale =>
  typeof value === 'string' && (APP_LOCALES as ReadonlyArray<string>).includes(value);

/**
 * Two catalogues sit side by side, and they are deliberately not merged.
 *
 * The flat root keys are the legacy dictionary the pre-Vue-3 views still read, in
 * `apps/frontend/i18n/`. It is frozen: nothing new goes there and the campaign work never edits it.
 *
 * Everything the new campaign UI says lives in ONE file per locale — `locales/poc.{en,ru}.json` —
 * mounted under `modules.*`. One file is a hard requirement: the copy is meant to be edited in a
 * single place, eventually through an editor rather than by hand, so splitting it per module would
 * defeat the point. Inside it the tree mirrors `src/modules/`, so a key reads as the path to the
 * code that renders it and back. The flat root already owns a `campaigns` string, which is why the
 * new tree cannot start there.
 */
// Two mount points. `ui.*` comes from `@hprofits/ui`, which names those keys and therefore owns
// their copy — the playground localises itself from the same export instead of re-authoring them.
// Everything the app itself says stays in ONE file per locale, mounted under `modules.*`.
//
// Only `en` is registered up front. The Russian catalogue is ~110 KB of copy that most sessions
// never render, and statically importing it put all of it in the entry chunk, ahead of first paint
// — `loadLocaleMessages` splits it into a chunk fetched when the locale is actually selected.
export const messages = {
  en: { ...legacyEn, modules: pocEn, ui: UI_MESSAGES.en },
};

export type AppMessageSchema = (typeof messages)['en'];

/**
 * Deliberately no legacy keys under a non-default locale. The pre-Vue-3 app is English-only by
 * product decision, and leaving them out is what enforces it: a legacy key misses in `ru`,
 * `fallbackLocale` serves the English one, and switching the language cannot touch those screens.
 * It also keeps ~37 KB of never-shipped Russian legacy copy out of the build entirely.
 */
export type AppLocaleMessages = Pick<AppMessageSchema, 'modules' | 'ui'>;

export const loadLocaleMessages = async (locale: AppLocale): Promise<AppLocaleMessages> => {
  if (locale === 'ru') {
    const { default: pocRu } = await import('./locales/poc.ru.json');
    return { modules: pocRu, ui: UI_MESSAGES.ru };
  }
  return messages.en;
};
